import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  MessageSquare, Sparkles, Hash, Smile, Eye, Wand2, X,
  ChevronDown, ChevronRight, BookOpen, Info,
} from 'lucide-react';
import { useTranslation } from '../lib/i18n';
import BrandVoiceAnalyzer from './BrandVoiceAnalyzer';
import CaptionPreview from './CaptionPreview';

/**
 * Caption style controls for the AutoPost settings page.
 *
 * Three layers, in order of impact:
 *   1. Top. Concise tone/CTA/hashtag/emoji presets. Most users only
 *      touch these. Defaults work for 90% of cases.
 *   2. Middle (collapsed). Custom hashtags (always-include/never-use)
 *      and free-form instructions. For users who want fine control.
 *   3. Bottom. Brand Voice Analyzer (AI-driven). User pastes a few
 *      of their best past posts → AI learns their voice → all future
 *      captions match it. This is the experimental flagship feature.
 *
 * Plus an inline "Vorschau Captions" button at the top that opens a
 * modal showing exactly what each platform would receive for a sample
 * property. Lets the user tune iteratively against real output before
 * committing settings.
 *
 * Props
 *   captionStyle . Current caption_style dict from auto_post_configs
 *   onChange(patch). Partial caption_style update
 *   propertyOptions. Array of {id, title} for the preview modal
 */
export default function CaptionStyleSection({
  captionStyle = {},
  onChange,
  propertyOptions = [],
  // Optional auto-save callback. When provided, the BrandVoiceAnalyzer's
  // "Stimme übernehmen" button persists the new voice immediately
  // instead of waiting for the user to remember to hit the global
  // Save button. Big trust win. Accepting a voice and tab-switching
  // away no longer loses the analysis.
  onAutoSave,
}) {
  const { t } = useTranslation();

  const style = {
    brand_voice: 'professional',
    cta_style: 'engagement',
    hashtag_aggression: 'standard',
    emoji_usage: 'moderate',
    custom_instructions: '',
    custom_hashtags: [],
    blocked_hashtags: [],
    learned_brand_voice: null,
    ...captionStyle,
  };

  const set = (patch) => onChange?.({ ...style, ...patch });

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [showVoiceAnalyzer, setShowVoiceAnalyzer] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [customTagInput, setCustomTagInput] = useState('');
  const [blockedTagInput, setBlockedTagInput] = useState('');

  // Pill-button preset selector. Reusable for the four main controls.
  // Renders a label + a row of selectable chip buttons. Active chip gets
  // violet accent; inactive chips stay quiet so the active state is
  // obvious at a glance.
  const PresetSelector = ({ label, hint, value, options, onPick, testIdPrefix }) => (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
        <span className="text-sm font-medium text-[#23211a]">{label}</span>
        {hint && <span className="text-[11px] text-[#8a8474] max-w-md">{hint}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onPick(opt.value)}
              data-testid={`${testIdPrefix}-${opt.value}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                active
                  ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50 shadow-[0_0_0_3px_rgba(139,92,246,0.08)]'
                  : 'bg-[#fffdf7] text-[#6f6a5c] border-[#d8d0bd] hover:border-[#1f4d3a]/50 hover:text-[#23211a]'
              }`}
              title={opt.description}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Hashtag chip. Reusable for both custom and blocked lists.
  const TagChip = ({ tag, onRemove, tone = 'violet' }) => (
    <span
      className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${
        tone === 'red'
          ? 'bg-[#f7e8e3] text-[#8c3a29] border-[#b3543f]/40'
          : 'bg-[#e9efe7] text-[#2e6b50] border-[#1f4d3a]/30'
      }`}
    >
      {tag}
      <button
        type="button"
        onClick={onRemove}
        aria-label="remove"
        className="hover:text-[#23211a]"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );

  const addCustomTag = () => {
    const tag = customTagInput.trim().replace(/^#+/, '');
    if (!tag) return;
    const normalized = `#${tag.replace(/\s+/g, '')}`;
    if (style.custom_hashtags.find((t) => t.toLowerCase() === normalized.toLowerCase())) {
      setCustomTagInput('');
      return;
    }
    set({ custom_hashtags: [...style.custom_hashtags, normalized] });
    setCustomTagInput('');
  };

  const addBlockedTag = () => {
    const tag = blockedTagInput.trim().replace(/^#+/, '');
    if (!tag) return;
    const normalized = `#${tag.replace(/\s+/g, '')}`;
    if (style.blocked_hashtags.find((t) => t.toLowerCase() === normalized.toLowerCase())) {
      setBlockedTagInput('');
      return;
    }
    set({ blocked_hashtags: [...style.blocked_hashtags, normalized] });
    setBlockedTagInput('');
  };

  const hasLearnedVoice = style.learned_brand_voice && (style.learned_brand_voice.tone || style.learned_brand_voice.summary);

  return (
    <>
      <Card className="border-[#d8d0bd] bg-[#fffdf7]">
        <CardContent className="p-5 space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-semibold text-[#23211a] flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#1f4d3a]" />
                {t('autoPost.captionStyle.title') || 'Caption-Stil'}
              </h3>
              <p className="text-xs text-[#6f6a5c] mt-0.5 max-w-md">
                {t('autoPost.captionStyle.subtitle') ||
                  'Wie soll die KI deine Captions schreiben? Jede Plattform bekommt eine eigene Variante.'}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowPreview(true)}
              className="border-[#d8d0bd] text-[#23211a] hover:bg-[#efe9db] shrink-0"
              data-testid="caption-style-preview-btn"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              {t('autoPost.captionStyle.preview') || 'Vorschau'}
            </Button>
          </div>

          {/* Top layer. The four main presets */}
          <div className="space-y-4">
            <PresetSelector
              label={t('autoPost.captionStyle.toneLabel') || 'Tonfall'}
              hint={t('autoPost.captionStyle.toneHint') ||
                'Beeinflusst die Stimme der KI quer über alle Plattformen.'}
              value={style.brand_voice}
              testIdPrefix="caption-tone"
              onPick={(v) => set({ brand_voice: v })}
              options={[
                { value: 'professional', label: t('autoPost.captionStyle.tone.professional') || 'Professionell' },
                { value: 'casual',       label: t('autoPost.captionStyle.tone.casual')       || 'Locker' },
                { value: 'luxury',       label: t('autoPost.captionStyle.tone.luxury')       || 'Luxus' },
                { value: 'friendly',     label: t('autoPost.captionStyle.tone.friendly')     || 'Freundlich-lokal' },
              ]}
            />

            <PresetSelector
              label={t('autoPost.captionStyle.ctaLabel') || 'Call-to-Action'}
              hint={t('autoPost.captionStyle.ctaHint') ||
                'Engagement schlägt Website-Klicks bei Social-Media-Reichweite.'}
              value={style.cta_style}
              testIdPrefix="caption-cta"
              onPick={(v) => set({ cta_style: v })}
              options={[
                { value: 'engagement', label: t('autoPost.captionStyle.cta.engagement') || 'Engagement (Tag, Save, DM)' },
                { value: 'website',    label: t('autoPost.captionStyle.cta.website')    || 'Website / Profil' },
                { value: 'dm',         label: t('autoPost.captionStyle.cta.dm')         || 'Direktnachricht' },
              ]}
            />

            <PresetSelector
              label={t('autoPost.captionStyle.hashtagLabel') || 'Hashtag-Menge'}
              hint={t('autoPost.captionStyle.hashtagHint') ||
                'Mehr ≠ besser. „Standard" basiert auf 2026 Research-Empfehlungen.'}
              value={style.hashtag_aggression}
              testIdPrefix="caption-hashtags"
              onPick={(v) => set({ hashtag_aggression: v })}
              options={[
                { value: 'conservative', label: t('autoPost.captionStyle.aggression.conservative') || 'Sparsam' },
                { value: 'standard',     label: t('autoPost.captionStyle.aggression.standard')     || 'Standard' },
                { value: 'aggressive',   label: t('autoPost.captionStyle.aggression.aggressive')   || 'Maximal' },
              ]}
            />

            <PresetSelector
              label={t('autoPost.captionStyle.emojiLabel') || 'Emoji-Dichte'}
              hint={t('autoPost.captionStyle.emojiHint') ||
                'LinkedIn bekommt sowieso nie Emojis. Egal was hier steht.'}
              value={style.emoji_usage}
              testIdPrefix="caption-emoji"
              onPick={(v) => set({ emoji_usage: v })}
              options={[
                { value: 'none',     label: t('autoPost.captionStyle.emoji.none')     || 'Keine' },
                { value: 'sparing',  label: t('autoPost.captionStyle.emoji.sparing')  || 'Sparsam' },
                { value: 'moderate', label: t('autoPost.captionStyle.emoji.moderate') || 'Moderat' },
                { value: 'generous', label: t('autoPost.captionStyle.emoji.generous') || 'Verspielt' },
              ]}
            />
          </div>

          {/* Brand voice. Flagship feature. Highlighted card so users
              notice it. State changes from "set up" to "learned" once
              the user runs the analyzer and accepts the result. */}
          <div className={`p-4 rounded-lg border ${
            hasLearnedVoice
              ? 'border-[#1e7a4f]/40 bg-[#1e7a4f]/5'
              : 'border-[#1f4d3a]/30 bg-[#f0f3ec]'
          }`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2.5 min-w-0">
                <Sparkles className={`w-4 h-4 mt-0.5 shrink-0 ${
                  hasLearnedVoice ? 'text-[#1e7a4f]' : 'text-[#1f4d3a]'
                }`} />
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${
                    hasLearnedVoice ? 'text-[#155c3b]' : 'text-[#23211a]'
                  }`}>
                    {hasLearnedVoice
                      ? (t('autoPost.captionStyle.voiceLearned') || 'Deine Stimme ist gelernt')
                      : (t('autoPost.captionStyle.voiceTitle') || 'Lass die KI deine Stimme lernen')
                    }
                  </p>
                  {hasLearnedVoice ? (
                    <p className="text-xs text-[#1e7a4f] mt-1">
                      {style.learned_brand_voice.summary || style.learned_brand_voice.tone}
                    </p>
                  ) : (
                    <p className="text-xs text-[#2e6b50]/80 mt-1">
                      {t('autoPost.captionStyle.voiceHint') ||
                        'Füge 3–10 deiner bisherigen Posts ein. Die KI analysiert deinen Stil und schreibt zukünftige Captions in deiner Stimme.'}
                    </p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setShowVoiceAnalyzer(true)}
                className={`shrink-0 ${
                  hasLearnedVoice
                    ? 'border-[#1e7a4f]/50 text-[#1e7a4f] hover:bg-[#e4f0e8]'
                    : 'border-[#1f4d3a]/50 text-[#2e6b50] hover:bg-[#e9efe7]'
                }`}
                data-testid="caption-style-voice-btn"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                {hasLearnedVoice
                  ? (t('autoPost.captionStyle.voiceUpdate') || 'Neu analysieren')
                  : (t('autoPost.captionStyle.voiceAnalyze') || 'Posts analysieren')
                }
              </Button>
            </div>
            {hasLearnedVoice && (
              <button
                type="button"
                onClick={() => set({ learned_brand_voice: null })}
                className="text-[11px] text-[#6f6a5c] hover:text-[#23211a] mt-3 underline-offset-2 hover:underline"
              >
                {t('autoPost.captionStyle.voiceClear') || 'Gelernte Stimme zurücksetzen'}
              </button>
            )}
          </div>

          {/* Advanced. Collapsed by default so the section doesn't
              feel overwhelming for users who just want to set the
              four main presets. */}
          <div>
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-[#6f6a5c] hover:text-[#23211a] transition"
              data-testid="caption-style-advanced-toggle"
            >
              {advancedOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {t('autoPost.captionStyle.advancedToggle') || 'Erweitert (Hashtags, eigene Anweisungen)'}
            </button>

            {advancedOpen && (
              <div className="mt-4 space-y-5">
                {/* Custom always-include hashtags */}
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
                    <span className="text-sm font-medium text-[#23211a] flex items-center gap-1.5">
                      <Hash className="w-3.5 h-3.5 text-[#1f4d3a]" />
                      {t('autoPost.captionStyle.customTagsLabel') || 'Immer einfügen'}
                    </span>
                    <span className="text-[11px] text-[#8a8474] max-w-md text-right">
                      {t('autoPost.captionStyle.customTagsHint') ||
                        'Deine Branded-Tags oder Stadt-Tags die jeder Post bekommen soll.'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {style.custom_hashtags.map((tag) => (
                      <TagChip
                        key={tag}
                        tag={tag}
                        onRemove={() =>
                          set({ custom_hashtags: style.custom_hashtags.filter((t) => t !== tag) })
                        }
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={customTagInput}
                      onChange={(e) => setCustomTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                      placeholder={t('autoPost.captionStyle.customTagsPlaceholder') || '#MeineMarke'}
                      className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] text-sm flex-1"
                      data-testid="caption-style-custom-tag-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addCustomTag}
                      className="border-[#d8d0bd] text-[#23211a] shrink-0"
                    >
                      {t('common.add') || 'Hinzufügen'}
                    </Button>
                  </div>
                </div>

                {/* Blocked hashtags */}
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
                    <span className="text-sm font-medium text-[#23211a] flex items-center gap-1.5">
                      <X className="w-3.5 h-3.5 text-[#b3543f]" />
                      {t('autoPost.captionStyle.blockedTagsLabel') || 'Nie verwenden'}
                    </span>
                    <span className="text-[11px] text-[#8a8474] max-w-md text-right">
                      {t('autoPost.captionStyle.blockedTagsHint') ||
                        'Tags die die KI generieren könnte, du aber nicht willst.'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {style.blocked_hashtags.map((tag) => (
                      <TagChip
                        key={tag}
                        tag={tag}
                        tone="red"
                        onRemove={() =>
                          set({ blocked_hashtags: style.blocked_hashtags.filter((t) => t !== tag) })
                        }
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={blockedTagInput}
                      onChange={(e) => setBlockedTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBlockedTag(); } }}
                      placeholder={t('autoPost.captionStyle.blockedTagsPlaceholder') || '#TagDenIchNichtMag'}
                      className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#b3543f] text-sm flex-1"
                      data-testid="caption-style-blocked-tag-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addBlockedTag}
                      className="border-[#d8d0bd] text-[#23211a] shrink-0"
                    >
                      {t('common.add') || 'Hinzufügen'}
                    </Button>
                  </div>
                </div>

                {/* Custom instructions. Free form. Power tool for users
                    who want to nail specific phrasing rules without
                    waiting for the post-analyzer. */}
                <div>
                  <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
                    <span className="text-sm font-medium text-[#23211a] flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-[#1f4d3a]" />
                      {t('autoPost.captionStyle.customInstructionsLabel') || 'Eigene Anweisungen'}
                    </span>
                    <span className="text-[11px] text-[#8a8474] max-w-md text-right">
                      {t('autoPost.captionStyle.customInstructionsHint') ||
                        'Wird in jeden KI-Prompt eingefügt. Beispiel: "Erwähne immer den Energieausweis-Status".'}
                    </span>
                  </div>
                  <textarea
                    value={style.custom_instructions}
                    onChange={(e) => set({ custom_instructions: e.target.value })}
                    placeholder={t('autoPost.captionStyle.customInstructionsPlaceholder') ||
                      'z.B. "Vermeide Buzzwords wie exklusiv oder einmalig. Erwähne immer dass Hund erlaubt ist."'}
                    rows={3}
                    maxLength={500}
                    className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a] focus:ring-2 focus:ring-[#1f4d3a]/20 resize-none"
                    data-testid="caption-style-custom-instructions"
                  />
                  <p className="text-[11px] text-[#8a8474] mt-1 text-right">
                    {style.custom_instructions.length}/500
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals. Managed at this level so the parent's setConfig flow
          stays clean (the section owns its caption_style sub-tree). */}
      {showVoiceAnalyzer && (
        <BrandVoiceAnalyzer
          onClose={() => setShowVoiceAnalyzer(false)}
          onAccept={(profile) => {
            const nextStyle = { ...style, learned_brand_voice: profile };
            // Push to local state (parent's config)…
            onChange?.(nextStyle);
            setShowVoiceAnalyzer(false);
            // …and if the parent wired auto-save, also persist
            // immediately. Otherwise the user has to remember to
            // click the global Save and may lose the voice if they
            // navigate away.
            onAutoSave?.(nextStyle);
          }}
          currentProfile={style.learned_brand_voice}
        />
      )}

      {showPreview && (
        <CaptionPreview
          onClose={() => setShowPreview(false)}
          captionStyleOverride={style}
          propertyOptions={propertyOptions}
        />
      )}
    </>
  );
}
